import { z } from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import type {
  TypedSchemaInput,
  TypedSchemaOutput,
} from "@/utils/utilityTypes.ts"
import { requiredTrimmedString } from "@/validation/sharedStringFieldSchemas"

const productModifierGroupFormSchema = z
  .object({
    label: requiredTrimmedString(
      "Please enter a modifier label"
    ).max(255),
    isShared: z.boolean(),
    minOptions: z.number().int().min(0),
    maxOptions: z.number().int().min(1),
    options: z.array(
      z.object({
        id: z.string().nullable().optional(),
        label: requiredTrimmedString(
          "Please enter an option label"
        ).max(60),
        priceOffset: z.number().int(),
        isSelected: z.boolean(),
      })
    ),
  })
  .superRefine((data, ctx) => {
    const addedOptionsLength = data.options.length
    if (data.minOptions > data.maxOptions) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maxOptions"],
        message:
          "Must be greater than the min options value",
      })
    }
    if (addedOptionsLength < data.minOptions) {
      ctx.addIssue({
        code: z.ZodIssueCode.too_small,
        minimum: data.minOptions,
        type: "number",
        inclusive: true,
        path: ["minOptions"],
        message:
          "Min Options cannot exceed the number of options",
      })
    }
  })

export type ProductModifierGroupFormInput =
  TypedSchemaInput<typeof productModifierGroupFormSchema>
export type ProductModifierGroupFormOutput =
  TypedSchemaOutput<typeof productModifierGroupFormSchema>

export const productModifierInitialFormValues = {
  label: "",
  isShared: false,
  minOptions: 0,
  maxOptions: 3,
  options: [
    {
      id: null,
      label: "",
      priceOffset: 0,
      isSelected: false,
    },
  ],
}

export const useProductModifierGroupForm = () =>
  useForm({
    validationSchema: toTypedSchema(
      productModifierGroupFormSchema
    ),
    initialValues: productModifierInitialFormValues,
  })
